import React, { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../../api/client';

/**
 * Recent sign-ins to this account (SRS #126): when, from where, on what.
 *
 * The server has recorded this all along — time, IP, the device the browser
 * reports, how the sign-in was made and when it ended — and an administrator
 * can see it on the Security screen. Nothing rendered it for the account
 * holder, which is what was reported: the log exists but the customer cannot
 * see it. Seeing your own sign-ins is how you notice one you did not make;
 * the panel above then signs that device out.
 */

interface LoginEvent {
  id: number;
  login_at: string | null;
  logout_at: string | null;
  ip_address: string | null;
  device: string | null;
  user_agent: string | null;
  method: string | null;
}

const PAGE = 10;

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined,
    { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const METHODS: Record<string, string> = {
  password: 'Password', google: 'Google', sso: 'Single sign-on',
  otp: 'One-time code', mfa: 'Password + code',
};

const SignInHistory: React.FC = () => {
  const [rows, setRows] = useState<LoginEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    apiRequest<{ items?: LoginEvent[] } | LoginEvent[]>('/api/v1/users/login-history')
      .then((r) => {
        setRows(Array.isArray(r) ? r : (r.items ?? []));
        setLoaded(true);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Could not load your sign-in history');
        setLoaded(true);
      });
  }, []);
  useEffect(() => { load(); }, [load]);

  const text = 'var(--color-text, var(--ds-text))';
  const muted = 'var(--color-text-muted, var(--ds-text-muted))';
  const border = 'var(--ds-border)';
  const shown = showAll ? rows : rows.slice(0, PAGE);

  return (
    <div style={{ marginTop: 18, borderTop: `1px solid ${border}`, paddingTop: 14 }}>
      <div style={{ fontWeight: 600, color: text, fontSize: 14 }}>Recent sign-ins</div>
      <div style={{ fontSize: 12, color: muted }}>
        Every sign-in to this account, newest first. Anything you don&apos;t recognise should be
        signed out above and your password changed.
      </div>

      {error && (
        <p style={{ color: 'var(--ds-danger-text)', fontSize: 12.5, margin: '8px 0 0' }}>{error}</p>
      )}
      {loaded && !error && rows.length === 0 && (
        <p data-testid="no-sign-ins" style={{ fontSize: 12.5, color: muted, margin: '8px 0 0' }}>
          No sign-ins recorded yet.
        </p>
      )}

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table data-testid="sign-in-history" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: muted }}>
                <th style={{ padding: '6px 10px 6px 0', fontWeight: 600 }}>When</th>
                <th style={{ padding: '6px 10px', fontWeight: 600 }}>Device</th>
                <th style={{ padding: '6px 10px', fontWeight: 600 }}>IP address</th>
                <th style={{ padding: '6px 10px', fontWeight: 600 }}>Method</th>
                <th style={{ padding: '6px 0 6px 10px', fontWeight: 600 }}>Signed out</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} style={{ borderTop: `1px solid ${border}`, color: text }}>
                  <td style={{ padding: '7px 10px 7px 0', whiteSpace: 'nowrap' }}>{when(r.login_at)}</td>
                  <td style={{ padding: '7px 10px' }} title={r.user_agent || ''}>
                    {r.device || <span style={{ color: muted }}>Not reported</span>}
                  </td>
                  <td style={{ padding: '7px 10px', fontVariantNumeric: 'tabular-nums' }}>
                    {r.ip_address || <span style={{ color: muted }}>—</span>}
                  </td>
                  <td style={{ padding: '7px 10px' }}>
                    {r.method ? (METHODS[r.method] ?? r.method) : <span style={{ color: muted }}>—</span>}
                  </td>
                  <td style={{ padding: '7px 0 7px 10px', whiteSpace: 'nowrap' }}>
                    {r.logout_at ? when(r.logout_at) : <span style={{ color: muted }}>still signed in</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > PAGE && (
            <button
              type="button"
              data-testid="toggle-sign-ins"
              onClick={() => setShowAll((v) => !v)}
              style={{ marginTop: 8, padding: '5px 10px', borderRadius: 8, border: `1px solid ${border}`,
                       background: 'transparent', cursor: 'pointer', fontSize: 12.5, color: text }}
            >
              {showAll ? 'Show fewer' : `Show all ${rows.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SignInHistory;
